/**
 * Complexity Detector
 * Automatically detects the complexity level of user requests
 * for smart token charging
 */

export type ComplexityLevel = 'simple' | 'moderate' | 'complex' | 'advanced';

export interface ComplexityAnalysis {
    level: ComplexityLevel;
    confidence: number;
    indicators: string[];
    score: number;
}

/**
 * Analyze message complexity
 */
export function detectComplexity(message: string, hasAttachments: boolean = false): ComplexityAnalysis {
    const indicators: string[] = [];
    let score = 0;

    // Normalize message
    const text = message.toLowerCase();
    const wordCount = text.split(/\s+/).length;

    // 1. Length-based scoring
    if (wordCount < 10) {
        score += 0;
        indicators.push('short_message');
    } else if (wordCount < 30) {
        score += 1;
        indicators.push('medium_message');
    } else if (wordCount < 100) {
        score += 2;
        indicators.push('long_message');
    } else {
        score += 3;
        indicators.push('very_long_message');
    }

    // 2. Simple question patterns (reduce score)
    const simplePatterns = [
        /^what is/i,
        /^define/i,
        /^who is/i,
        /^when did/i,
        /^where is/i,
        /^yes or no/i,
        /^true or false/i,
    ];
    if (simplePatterns.some(p => p.test(message))) {
        score -= 1;
        indicators.push('simple_question');
    }

    // 3. Complex calculation indicators
    const calculationPatterns = [
        /calculat/i,
        /compute/i,
        /solve/i,
        /determine/i,
        /find.*value/i,
        /how much/i,
        /how many/i,
        /\d+\s*[+\-×÷*/]\s*\d+/,
        /percentage/i,
        /ratio/i,
        /proportion/i,
    ];
    const calcMatches = calculationPatterns.filter(p => p.test(message)).length;
    if (calcMatches > 0) {
        score += calcMatches;
        indicators.push('calculation_required');
    }

    // 4. Multi-step indicators
    const multiStepPatterns = [
        /step.*by.*step/i,
        /first.*then.*finally/i,
        /multiple.*step/i,
        /process/i,
        /procedure/i,
        /and.*also/i,
        /both.*and/i,
    ];
    const multiStepMatches = multiStepPatterns.filter(p => p.test(message)).length;
    if (multiStepMatches > 0) {
        score += multiStepMatches * 1.5;
        indicators.push('multi_step');
    }

    // 5. Analysis/comparison indicators
    const analysisPatterns = [
        /analyz/i,
        /compare/i,
        /contrast/i,
        /difference.*between/i,
        /relationship/i,
        /evaluate/i,
        /assess/i,
        /critique/i,
        /examine/i,
    ];
    const analysisMatches = analysisPatterns.filter(p => p.test(message)).length;
    if (analysisMatches > 0) {
        score += analysisMatches * 2;
        indicators.push('analysis_required');
    }

    // 6. Professional/technical indicators
    const professionalPatterns = [
        /pfrs/i,
        /pas\s*\d+/i,
        /gaap/i,
        /ifrs/i,
        /consolidat/i,
        /amortiz/i,
        /depreciat/i,
        /liquidat/i,
        /reconcil/i,
        /variance/i,
        /hedge/i,
        /derivative/i,
        /deferred.*tax/i,
        /business.*combination/i,
    ];
    const professionalMatches = professionalPatterns.filter(p => p.test(message)).length;
    if (professionalMatches > 0) {
        score += professionalMatches * 2.5;
        indicators.push('professional_topic');
    }

    // 7. Detailed explanation requests
    const detailPatterns = [
        /explain.*detail/i,
        /in.*depth/i,
        /comprehensive/i,
        /thorough/i,
        /elaborate/i,
        /justify/i,
        /prove/i,
        /show.*work/i,
        /with.*example/i,
    ];
    const detailMatches = detailPatterns.filter(p => p.test(message)).length;
    if (detailMatches > 0) {
        score += detailMatches * 1.5;
        indicators.push('detailed_explanation');
    }

    // 8. Multiple concepts
    const conceptCount = (text.match(/\band\b/gi) || []).length;
    if (conceptCount > 2) {
        score += conceptCount * 0.5;
        indicators.push('multiple_concepts');
    }

    // 9. Attachment complexity
    if (hasAttachments) {
        score += 2;
        indicators.push('has_attachments');
    }

    // 10. Question marks (multiple questions)
    const questionCount = (text.match(/\?/g) || []).length;
    if (questionCount > 1) {
        score += questionCount * 0.5;
        indicators.push('multiple_questions');
    }

    // Determine complexity level based on score
    let level: ComplexityLevel;
    let confidence: number;

    if (score < 2) {
        level = 'simple';
        confidence = 0.9;
    } else if (score < 5) {
        level = 'moderate';
        confidence = 0.8;
    } else if (score < 10) {
        level = 'complex';
        confidence = 0.85;
    } else {
        level = 'advanced';
        confidence = 0.9;
    }

    return {
        level,
        confidence,
        indicators,
        score,
    };
}

/**
 * Detect model requirement based on complexity
 */
export function detectRequiredModel(complexity: ComplexityLevel, isPremium: boolean): 'flash' | 'pro' {
    if (complexity === 'advanced' && isPremium) {
        return 'pro';
    }
    if (complexity === 'complex' && isPremium) {
        // Use pro for complex tasks if premium
        return 'pro';
    }
    return 'flash';
}

/**
 * Estimate output length based on complexity
 */
export function estimateOutputTokens(complexity: ComplexityLevel, hasAttachments: boolean): number {
    const baseEstimates = {
        simple: 200,
        moderate: 500,
        complex: 1000,
        advanced: 2000,
    };

    let estimate = baseEstimates[complexity];

    // Attachments typically require longer responses
    if (hasAttachments) {
        estimate *= 1.5;
    }

    return Math.ceil(estimate);
}

/**
 * Get human-readable complexity description
 */
export function getComplexityDescription(level: ComplexityLevel): string {
    const descriptions = {
        simple: 'Simple question - quick answer',
        moderate: 'Standard question - detailed response',
        complex: 'Complex analysis - multi-step solution',
        advanced: 'Advanced professional topic - comprehensive analysis',
    };
    return descriptions[level];
}

/**
 * Analyze quiz/exam complexity
 */
export function detectQuizComplexity(
    itemCount: number,
    difficulty: string,
    focus: string
): ComplexityLevel {
    let score = 0;

    // Item count
    if (itemCount <= 5) score += 1;
    else if (itemCount <= 20) score += 2;
    else if (itemCount <= 50) score += 3;
    else score += 4;

    // Difficulty
    if (difficulty === 'easy') score += 0;
    else if (difficulty === 'medium' || difficulty === 'hard') score += 2;
    else score += 3; // very_hard, cpale_level

    // Focus
    if (focus === 'theories') score += 1;
    else if (focus === 'computations') score += 2;
    else score += 1.5; // mixed

    if (score < 3) return 'simple';
    if (score < 6) return 'moderate';
    if (score < 9) return 'complex';
    return 'advanced';
}
